import { useQueryClient } from '@tanstack/react-query'
import { LogOut, UserCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { ContactUs } from './components/ContactUs'
import { Button } from './components/ui/button'
import { useIsMobile } from './hooks/useIsMobile'
import icon from './imgs/Logo_f.svg'
import { QUERY_KEYS } from './lib/queryKeys'
import { cn } from './lib/utils'
import { RoutePath } from './RoutePath'
import { setIsAuthenticated } from './store/appSlice'

export default function NavBar() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const { data: loggedUser } = useCurrentUser()
  const { i18n, t } = useTranslation()
  const [isShowContactUs, setShowContactUs] = useState(false)
  const [isMobileMenuActive, setIsMobileMenuActive] = useState(false)
  const queryClient = useQueryClient()

  // @TODO: remove this once we have a proper language toggle
  // eslint-disable-next-line unused-imports/no-unused-vars
  const toggleLanguage = () => {
    const nextLang = i18n.language === 'he' ? 'en' : 'he'
    i18n.changeLanguage(nextLang)
    setIsMobileMenuActive(false)
  }

  const logOutHandler = async () => {
    try {
      localStorage.removeItem('cognito-tokens')
      dispatch(setIsAuthenticated(false))
      queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], null)
      queryClient.clear()
      navigate('/')
    } catch (error) {
      throw error
    }
  }
  const onSetShowContactUs = () => {
    setIsMobileMenuActive(false)
    setShowContactUs(true)
  }
  function mobileMenu() {
    if (!isMobile) return
    setIsMobileMenuActive(prev => !prev)
  }
  const linkClasses = 'text-center cursor-pointer hover:underline text-[15px] font-normal text-black no-underline hover:text-[#fcde00]'
  return (
    <header dir='rtl' className='h-[var(--navbar-height)] w-full border-b border-[#eaeaec] bg-white'>
      <nav className='flex items-center justify-between px-2.5 py-2.5'>
        <div
          className={cn(
            'me-1 block cursor-pointer md:hidden',
            isMobileMenuActive &&
              '[&>span:nth-child(1)]:translate-y-2 [&>span:nth-child(1)]:rotate-45 [&>span:nth-child(2)]:opacity-0 [&>span:nth-child(3)]:-translate-y-2 [&>span:nth-child(3)]:-rotate-45'
          )}
          onClick={() => {
            mobileMenu()
          }}
        >
          <span className='mx-auto my-1.5 block h-0.5 w-[25px] bg-black transition-all duration-300 ease-in-out'></span>
          <span className='mx-auto my-1.5 block h-0.5 w-[25px] bg-black transition-all duration-300 ease-in-out'></span>
          <span className='mx-auto my-1.5 block h-0.5 w-[25px] bg-black transition-all duration-300 ease-in-out'></span>
        </div>

        <ul
          className={cn(
            'mb-0 list-none bg-white md:gap-8 md:bg-transparent [&>li]:py-4 md:[&>li]:py-0',
            'fixed top-[56px] flex max-h-[90vh] w-full flex-col items-center overflow-y-scroll rounded-b-[10px] ps-0 text-center shadow-[0_10px_27px_rgba(0,0,0,0.05)] transition-all duration-300',
            isMobileMenuActive ? 'start-0 z-[1000]' : '-start-full',
            'max-h-none md:static md:w-auto md:flex-row md:justify-between md:overflow-visible md:rounded-none md:ps-0 md:shadow-none'
          )}
        >
          {/* <li>
            <button type='button' onClick={toggleLanguage} className={linkClasses} title={i18n.language === 'he' ? 'English' : 'עברית'}>
              {i18n.language === 'he' ? '🇺🇸' : '🇮🇱'}
            </button>
          </li> */}
          <li>
            <a href='https://www.shiptanbul.com/about' className={linkClasses}>
              {t('nav.niceToMeet')}
            </a>
          </li>
          <li>
            <a href='https://www.shiptanbul.com/start-here' className={linkClasses}>
              {t('nav.howItWorks')}
            </a>
          </li>
          <li>
            <a href='https://shiptanbul.com/shops/usa' className={linkClasses}>
              {t('nav.recommendedShops')}
            </a>
          </li>
          <li>
            <a href='https://www.shiptanbul.com/knowledge-center' className={linkClasses}>
              {t('nav.knowledgeCenter')}
            </a>
          </li>
          <li>
            <a href='https://shiptanbul.com/calculator' className={linkClasses}>
              {t('nav.calculators')}
            </a>
          </li>
          <li>
            <button className={linkClasses} onClick={onSetShowContactUs}>
              {t('nav.contactUs')}
            </button>
          </li>

          {loggedUser ? (
            <>
              <Button
                variant='ghost'
                size='sm'
                className='order-last md:order-first'
                onClick={() => {
                  logOutHandler()
                  mobileMenu()
                }}
              >
                <LogOut className='size-4' />
              </Button>
              <li className='order-last -ms-3 mb-4 md:order-first md:ms-0 md:mb-0'>
                <Link
                  to={RoutePath.ClientProfile}
                  onClick={() => {
                    mobileMenu()
                  }}
                >
                  <Button icon={<UserCircle />} size='sm'>
                    {loggedUser.name}
                  </Button>
                </Link>
              </li>
            </>
          ) : (
            <li className='order-last md:order-first'>
              <Link
                to='/'
                className={linkClasses}
                onClick={() => {
                  mobileMenu()
                }}
              >
                <Button style={{ maxWidth: '110px', fontSize: '15px' }}>{t('nav.login')}</Button>
              </Link>
            </li>
          )}
        </ul>

        <a href='http://www.shiptanbul.com/'>
          <img alt='icon' width='200px' src={icon} />
        </a>
      </nav>
      <ContactUs show={isShowContactUs} setShow={setShowContactUs} />
    </header>
  )
}
